import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, createSearchParams } from "react-router-dom";
import {
  getInvoiceList,
  getTenantList,
  getPropertyList,
} from "./Invoices.slice";
import Layout from "./Layout";
import Table, { type TableColumnConfig } from "../../../components/Table/Table";
import Badge from "../../../components/Badge";
import Panel from "../../../components/Panel";
import ComboboxAutocomplete from "../../../components/Form/ComboboxAutocomplete";
import Input from "../../../components/Form/Input";
import InlineEditDatePicker from "../../../components/Form/InlineEditDatePicker";
import qs from "qs";
import {
  formatUTCDate,
  formatCurrency,
  makeEnumReadable,
  dateFromUTCDateString,
  formatBillingPeriod,
} from "../../../util/string";
import { InvoiceStatus, InvoiceSummaries, CollectionsInvoice, StatementType } from '../../../apis/types'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import { getStatementApi, updateInvoiceApi, checkDOBAvailabilityBulkApi, downloadPGEDOBWithFilenameApi, bulkDownloadInvoicesApi } from '../../../apis/apis'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import Button from '../../../components/Form/Button'
import TableCheckMark from '../../file_explorer/components/TableCheckMark'
import { format } from 'date-fns'
import {
  getStatementTypeVisibility,
  hasDistinctLateByDate,
  InvoiceStatementIcons,
  statementColumnWidthFor,
} from './invoiceStatementIcons'

// Helper to get disabled reason for date editing
const getDateEditDisabledReason = (invoice: CollectionsInvoice): string | undefined => {
  if (!invoice.canEditDates) {
    // Non-latest invoices are just quietly disabled (no tooltip needed)
    if (!invoice.isLatestInvoice) {
      return undefined
    }
    if (['PAID', 'VOID', 'PAID_EXTERNALLY', 'SKIPPED', 'BALANCE_CARRY_FORWARD'].includes(invoice.status)) {
      return `Cannot edit dates for ${invoice.status} invoice`
    }
    if (invoice.remainingBalance <= 0) {
      return 'Cannot edit dates for invoice with zero balance'
    }
    return 'Cannot edit dates due to pending payment'
  }
  return undefined
}

// Helper to check for date warnings
const createDateWarningCheck = (invoice: CollectionsInvoice, field: 'dueDate' | 'lateByDate') => {
  return (value: string | null): string | null => {
    if (!value) return null
    const newDate = new Date(value)
    const billingEnd = new Date(invoice.billingEnd)
    const diffDays = Math.round((newDate.getTime() - billingEnd.getTime()) / (1000 * 60 * 60 * 24))

    if (field === 'dueDate' && diffDays > 90) {
      return `Warning: Due date is ${diffDays} days from billing period end (>90 days). Continue?`
    }
    if (field === 'lateByDate' && diffDays > 120) {
      return `Warning: Late by date is ${diffDays} days from billing period end (>120 days). Continue?`
    }
    return null
  }
}

export interface InvoicesURLParams {
  propertyId: string | undefined
  tenantId: string | undefined
  status: string | undefined
  invoiceNumber: string | undefined
}

const Invoices: React.FC = () => {
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)
  const [propertyId, setPropertyId] = useState<string | undefined>(searchParams.get('propertyId') ?? undefined)
  const [tenantId, setTenantId] = useState<string | undefined>(searchParams.get('tenantId') ?? undefined)
  const [status, setStatus] = useState<InvoiceStatus | undefined>(
    (searchParams.get('status') as InvoiceStatus | null) ?? undefined
  )
  const [invoiceNumber, setInvoiceNumber] = useState<string | undefined>(searchParams.get('invoiceNumber') ?? undefined)
  const [dobAvailability, setDobAvailability] = useState<Record<string, boolean>>({})
  const [utilityLookup, setUtilityLookup] = useState<Record<string, string>>({})
  const [invoicesChecked, setInvoicesChecked] = useState<Set<string>>(new Set())
  const [isDownloading, setIsDownloading] = useState(false)
  const [listLoading, setListLoading] = useState(true)
  const listLoadGeneration = useRef(0)
  const tenantAccountId = searchParams.get('tenantAccountId') ?? undefined

  // Check if user has permission to edit invoice dates
  const hasEditInvoicePermission = (): boolean => (permissions != null ? permissions.includes('billing:edit:invoice') : false)

  // TODO To reenable the "Trailing 30-day totals" feature remove this flag here and stop the Panel
  // below from being conditionally rendered based on it
  const isTrailing30DayTotalsDisabled = true
  // TODO Also delete this hardcoded InvoiceSummary and import summary below on line 50
  // TODO Also uncomment the call to getInvoiceSummary() on line 56
  const summary: InvoiceSummaries | undefined = {
    PENDING: { number: 0, amount: 0 },
    PROCESSING: { number: 0, amount: 0 },
    PAID: { number: 0, amount: 0 },
    PAST_DUE: { number: 0, amount: 0 },
    VOID: { number: 0, amount: 0 },
    PAID_EXTERNALLY: { number: 0, amount: 0 },
    BALANCE_CARRY_FORWARD: { number: 0, amount: 0 },
    SKIPPED: { number: 0, amount: 0 },
  }
  // TODO Also go to src/features/collections/invoices/Invoices.slice.tsx and delete
  // the hardcoded api response for getInvoiceSummary()

  const navigate = useNavigate()
  // TODO import summary here to reenable "Trailing 30-day totals" feature
  const { invoices, tenants, properties } = useAppSelector((state) => state.invoices)

  useEffect(() => {
    // void dispatch(getInvoiceSummary())
    void dispatch(getTenantList())
    void dispatch(getPropertyList())
  }, [dispatch])

  const loadInvoices = useCallback(
    (page: number) => {
      const generation = ++listLoadGeneration.current
      setInvoicesChecked(new Set())
      setListLoading(true)
      void dispatch(
        getInvoiceList({
          page,
          resultsPerPage: 20,
          propertyId,
          tenantId,
          tenantAccountId,
          status,
          invoiceNumber,
        })
      ).finally(() => {
        if (generation === listLoadGeneration.current) {
          setListLoading(false)
        }
      })
    },
    [dispatch, invoiceNumber, propertyId, status, tenantAccountId, tenantId]
  )

  useEffect(() => {
    loadInvoices(0)
  }, [loadInvoices])

  useEffect(() => {
    const nextSearch = qs.stringify(
      { propertyId, tenantId, tenantAccountId, status, invoiceNumber },
      { addQueryPrefix: true, skipNulls: true }
    )
    if (nextSearch !== search) {
      navigate(`/collections/invoices${nextSearch}`, { replace: true })
    }
  }, [propertyId, tenantId, tenantAccountId, status, invoiceNumber, search, navigate])

  // Check DOB availability for invoices when tenant or account is filtered
  useEffect(() => {
    if ((tenantId || tenantAccountId) && invoices?.results && invoices.results.length > 0) {
      const checkDOBForInvoices = async () => {
        try {
          // Group invoices by tenant account ID
          const invoicesByAccount = invoices.results.reduce(
            (acc, invoice) => {
              if (!acc[invoice.tenantAccountId]) {
                acc[invoice.tenantAccountId] = []
              }
              acc[invoice.tenantAccountId].push(invoice)
              return acc
            },
            {} as Record<string, CollectionsInvoice[]>
          )

          // Build utility lookup from invoice results (no additional API calls needed)
          const newUtilityLookup: Record<string, string> = {}
          invoices.results.forEach((invoice) => {
            if (invoice.tenant.utilityName && invoice.tenantAccountId) {
              newUtilityLookup[invoice.tenantAccountId] = invoice.tenant.utilityName
            }
          })
          setUtilityLookup(newUtilityLookup)

          // Check DOB availability for each PG&E account
          let allDobAvailability: Record<string, boolean> = {}

          for (const [accountId, accountInvoices] of Object.entries(invoicesByAccount)) {
            if (newUtilityLookup[accountId] === 'PG&E') {
              const dateRanges = accountInvoices.map((invoice) => ({
                billingStart: invoice.billingStart,
                billingEnd: invoice.billingEnd,
              }))

              const availabilityByDateRange = await checkDOBAvailabilityBulkApi(accountId, dateRanges)

              // Map results back to invoice IDs
              accountInvoices.forEach((invoice) => {
                const dateRangeKey = `${invoice.billingStart}-${invoice.billingEnd}`
                allDobAvailability[invoice.id] = availabilityByDateRange[dateRangeKey] || false
              })
            }
          }

          setDobAvailability(allDobAvailability)
        } catch (error) {
          console.error('Failed to check DOB availability:', error)
          setDobAvailability({})
        }
      }

      checkDOBForInvoices()
    }
  }, [tenantId, tenantAccountId, invoices])

  const hasTenantContextForDob = Boolean(tenantId || tenantAccountId)
  const statementTypeVisibility = useMemo(
    () =>
      getStatementTypeVisibility(
        invoices?.results ?? [],
        dobAvailability,
        (row) => utilityLookup[row.tenantAccountId] === 'PG&E',
        hasTenantContextForDob
      ),
    [invoices?.results, dobAvailability, utilityLookup, hasTenantContextForDob]
  )

  const handlePaginatorClick = (page: number): void => {
    loadInvoices(page)
  }

  // Refresh current page of invoices after an update
  const refreshInvoices = useCallback(() => {
    const generation = ++listLoadGeneration.current
    setListLoading(true)
    void dispatch(
      getInvoiceList({
        page: invoices?.page ?? 0,
        resultsPerPage: 20,
        propertyId,
        tenantId,
        tenantAccountId,
        status,
        invoiceNumber,
      })
    ).finally(() => {
      if (generation === listLoadGeneration.current) {
        setListLoading(false)
      }
    })
  }, [dispatch, invoices?.page, propertyId, tenantId, tenantAccountId, status, invoiceNumber])

  const statusSortOrder = ['PAST_DUE', 'BALANCE_CARRY_FORWARD', 'PENDING', 'PROCESSING', 'PAID', 'PAID_EXTERNALLY', 'VOID']

  const statusSortFunction = (a: string, b: string): number => statusSortOrder.indexOf(a) - statusSortOrder.indexOf(b)

  const openStatement = async (statementId: string, type: StatementType) => {
    try {
      const statementFile = await getStatementApi(statementId, type)
      const statementFileBlob: Blob = new Blob([statementFile], {
        type: 'application/pdf',
      })
      const statementFileUrl: string = window.URL.createObjectURL(statementFileBlob)
      window.open(statementFileUrl, '_blank')
    } catch (e) {
      await dispatch(
        pushToast({
          type: 'error',
          message: 'Error loading statement',
          description: `${
            (e as XMLHttpRequest).response?.data?.message !== undefined ? `${(e as XMLHttpRequest).response?.data?.message as string}:` : 'Error:'
          } ${(e as { message: string }).message}`,
        })
      )
    }
  }

  const handleDOBDownload = async (invoice: CollectionsInvoice) => {
    try {
      const { data, filename } = await downloadPGEDOBWithFilenameApi(invoice.tenantAccountId, invoice.billingStart, invoice.billingEnd)

      // Determine if it's a PDF or ZIP based on filename
      const isPDF = filename.endsWith('.pdf')

      if (isPDF) {
        // For PDFs: download with proper filename but try to open in browser
        const blob = new Blob([data], {
          type: 'application/pdf',
        })
        const url = window.URL.createObjectURL(blob)

        // Create and click download link with proper filename
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.target = '_blank' // Try to open in new tab if browser supports it

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up after a delay to allow the download to start
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 1000)
      } else {
        // For ZIPs: download with proper filename
        const blob = new Blob([data], {
          type: 'application/zip',
        })
        const url = window.URL.createObjectURL(blob)

        // Create and click download link
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up
        window.URL.revokeObjectURL(url)
      }
    } catch (error: any) {
      console.error('Failed to download DOB:', error)
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to download DOB',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        })
      )
    }
  }

  const handleHeaderCheck = () => {
    if (invoicesChecked.size === (invoices?.results?.length ?? 0)) {
      setInvoicesChecked(new Set())
    } else {
      setInvoicesChecked(new Set(invoices?.results?.map((inv) => inv.id) ?? []))
    }
  }

  const handleRowCheck = (invoiceId: string) => {
    setInvoicesChecked((prev) => {
      const next = new Set(prev)
      if (next.has(invoiceId)) next.delete(invoiceId)
      else next.add(invoiceId)
      return next
    })
  }

  const handleBulkDownload = async (type: StatementType) => {
    if (invoicesChecked.size === 0) return
    setIsDownloading(true)
    try {
      const blob = await bulkDownloadInvoicesApi(Array.from(invoicesChecked), type)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/zip' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `invoices_${type}_${format(new Date(), 'yyyy-MM-dd_HH_mm_ss')}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      dispatch(pushToast({ type: 'success', message: `Downloaded ${invoicesChecked.size} invoice(s)` }))
    } catch (e: unknown) {
      const axiosError = e as { response?: { data?: { message?: string } }; message?: string }
      dispatch(
        pushToast({
          type: 'error',
          message: 'Error downloading invoices',
          description: axiosError.response?.data?.message ?? axiosError.message,
        })
      )
    } finally {
      setIsDownloading(false)
    }
  }

  const invoiceRows = invoices?.results ?? []
  const showLateByColumn = hasDistinctLateByDate(invoiceRows)
  const statementColumnWidth = statementColumnWidthFor(statementTypeVisibility)

  const colConfig = useMemo((): TableColumnConfig<CollectionsInvoice>[] => {
    const columns: TableColumnConfig<CollectionsInvoice>[] = [
      {
        label: null,
        renderHeader: () => (
          <TableCheckMark
            id="header-check"
            checked={invoicesChecked.size > 0 && invoicesChecked.size === (invoices?.results?.length ?? 0)}
            onClick={handleHeaderCheck}
          />
        ),
        render: (rec) => (
          <TableCheckMark id={`check-${rec.id}`} checked={invoicesChecked.has(rec.id)} onClick={() => handleRowCheck(rec.id)} />
        ),
      },
      {
        label: 'Tenant / Property',
        render: (rec) => (
          <>
            <span className="font-medium">{rec.tenant.name}</span>
            <br />
            <span>{rec.tenant.propertyName}</span>
          </>
        ),
      },
      {
        label: (
          <>
            Account
            <br />
            Number
          </>
        ),
        render: (rec) => (
          <Link
            to={{
              pathname: `/tenant/tenants/${rec.tenant.id}`,
              search: `?${createSearchParams({
                serviceAccountNumber: rec.tenantAccountNumber,
                tab: '5',
              }).toString()}`,
            }}
            className="text-indigo-500 dark:text-indigo-400"
          >
            {rec.tenantAccountNumber}
          </Link>
        ),
      },
      {
        label: (
          <>
            Statement
            <br />
            Number
          </>
        ),
        render: (rec) => rec.number,
      },
      {
        label: (
          <>
            Statement
            <br />
            Date
          </>
        ),
        render: (rec) => formatUTCDate(new Date(rec.statementDate)),
      },
      {
        label: (
          <>
            Billing
            <br />
            Period
          </>
        ),
        render: (rec) => formatBillingPeriod(rec.billingStart, rec.billingEnd),
      },
      {
        label: (
          <>
            Due
            <br />
            Date
          </>
        ),
        render: (rec) => (
          <InlineEditDatePicker
            name="dueDate"
            label="Due Date"
            hideLabel
            displayFormat="monthDay"
            initialValue={rec.due}
            minDate={new Date()}
            disabled={!rec.canEditDates || !hasEditInvoicePermission()}
            disabledReason={getDateEditDisabledReason(rec)}
            warningCheck={createDateWarningCheck(rec, 'dueDate')}
            handleSubmit={async (newDueDate) => {
              await updateInvoiceApi(rec.id, { dueDate: newDueDate })
              refreshInvoices()
            }}
          />
        ),
      },
    ]

    if (showLateByColumn) {
      columns.push({
        label: (
          <>
            Late By
            <br />
            Date
          </>
        ),
        render: (rec) => (
          <InlineEditDatePicker
            name="lateByDate"
            label="Late By Date"
            hideLabel
            displayFormat="monthDay"
            initialValue={rec.lateBy}
            minDate={dateFromUTCDateString(rec.due) ?? new Date(rec.due)}
            disabled={!rec.canEditDates || !hasEditInvoicePermission()}
            disabledReason={getDateEditDisabledReason(rec)}
            allowClear
            clearLabel="Same as Due"
            warningCheck={createDateWarningCheck(rec, 'lateByDate')}
            handleSubmit={async (newLateByDate) => {
              await updateInvoiceApi(rec.id, { lateByDate: newLateByDate })
              refreshInvoices()
            }}
          />
        ),
      })
    }

    columns.push(
      {
        label: (
          <>
            Period
            <br />
            Charges
          </>
        ),
        render: (rec) => formatCurrency(rec.periodChargeAccrual),
      },
      {
        label: (
          <>
            Period Due
            <br />
            Balance
          </>
        ),
        render: (rec) => formatCurrency(rec.unpaidInvoiceBalance),
      },
      {
        label: 'DPD When Paid',
        render: (rec) => rec.daysPastDue,
      }
    )

    columns.push(
      {
        label: 'Payment Status',
        render: (rec) => <Badge status={rec.paymentStatus} />,
      },
      {
        label: (
          <>
            Amount
            <br />
            Due
          </>
        ),
        render: (rec) => formatCurrency(rec.amountDue),
      },
      {
        label: 'Stmts',
        headerTitle: 'Statements (King, Utility, DOB, OptiRate)',
        columnWidth: statementColumnWidth,
        cellClassName: 'whitespace-nowrap',
        render: (rec) => (
          <InvoiceStatementIcons
            invoice={rec}
            visibility={statementTypeVisibility}
            isPgeInvoice={utilityLookup[rec.tenantAccountId] === 'PG&E'}
            dobAvailable={Boolean(hasTenantContextForDob && dobAvailability[rec.id])}
            onOpenKing={async () => await openStatement(rec.id, 'king')}
            onOpenUtility={async () => await openStatement(rec.id, 'utility')}
            onDownloadDob={async () => await handleDOBDownload(rec)}
            onOpenOptiRate={async () => await openStatement(rec.id, 'optirate_audit')}
          />
        ),
      },
      {
        label: (
          <>
            Payment
            <br />
            Method
          </>
        ),
        headerTitle: 'Payment Method',
        columnWidth: 168,
        cellClassName: 'overflow-hidden',
        render: (rec) => {
          if (rec.paymentMethod === null) {
            return null
          }

          let method: string | null = null
          if (rec.paymentMethod.type === 'CHECK') {
            method = 'Check'
          } else if (rec.paymentMethod.type === 'DIRECT_DEPOSIT') {
            method = 'Direct deposit'
          } else if (rec.paymentMethod.providerName !== undefined) {
            method = `${rec.paymentMethod.providerName} ${rec.paymentMethod.last4 !== undefined ? `(••••${rec.paymentMethod.last4})` : ''}`
          }

          if (method == null) {
            return null
          }

          return (
            <span className="block truncate max-w-[160px]" title={method}>
              {method}
            </span>
          )
        },
      },
      {
        label: 'Memo',
        render: (rec) => rec.memo,
      },
      {
        label: 'Invoice Status',
        render: (rec) => <Badge status={rec.status} />,
      }
    )

    return columns
  }, [
    invoices?.results,
    invoicesChecked,
    showLateByColumn,
    statementColumnWidth,
    statementTypeVisibility,
    utilityLookup,
    hasTenantContextForDob,
    dobAvailability,
    hasEditInvoicePermission,
    refreshInvoices,
  ])

  return (
    <>
      <Layout title="Invoices">
        {!isTrailing30DayTotalsDisabled && (
          <Panel header="Trailing 30-day totals" className="mb-5">
            <div className="grid grid-flow-col text-center">
              {(Object.keys(summary ?? {}) as Array<keyof InvoiceSummaries>).sort(statusSortFunction).map((status, index) => (
                <div key={`status-${index}`}>
                  <div className="font-medium">
                    <Badge status={status} />
                  </div>
                  <div className="text-2xl">{summary?.[status]?.number}</div>
                  <div className="">{formatCurrency(summary?.[status]?.amount ?? 0)}</div>
                </div>
              ))}
            </div>
          </Panel>
        )}
        <Panel header="Filters" className="mb-5">
          <div className="grid grid-auto-flow grid-cols-4 gap-3">
            {/* <QuickSearch title="Tenant name" initialValue={quickSearchTerm} handleChange={handleQuickSearchChange} /> */}
            <div>
              <label htmlFor="propertyNames" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Property
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Properties"
                  value={propertyId ?? ''}
                  handleChange={(value: string | null | undefined) => setPropertyId(value || undefined)}
                  data={properties}
                />
              )}
            </div>
            <div>
              <label htmlFor="tenantNames" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Tenant
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Tenants"
                  value={tenantId ?? ''}
                  handleChange={(value: string | null | undefined) => setTenantId(value || undefined)}
                  data={tenants}
                />
              )}
            </div>
            <div>
              <label htmlFor="statusType" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                Invoice Status
              </label>
              {properties !== undefined && (
                <ComboboxAutocomplete
                  labelPlural="Invoice Statuses"
                  value={status ?? ''}
                  handleChange={(value: string | null | undefined) =>
                    setStatus((value || undefined) as InvoiceStatus | undefined)
                  }
                  data={Object.keys(summary ?? {}).map((status: string) => ({
                    id: status,
                    name: makeEnumReadable(status),
                  }))}
                />
              )}
            </div>
            <div>
              <Input
                label="Statement Number (exact match)"
                value={invoiceNumber ?? ''}
                placeholder="e.g., 12345"
                onChange={(e) => setInvoiceNumber(e.target.value || undefined)}
              />
            </div>
          </div>
        </Panel>
        {invoicesChecked.size > 0 && (
          <div className="flex items-center px-3 py-3 mb-2 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 mr-4">
              {invoicesChecked.size} invoice(s) selected
            </span>
            <Button type="button" onClick={async () => await handleBulkDownload('king')} disabled={isDownloading}>
              {isDownloading ? 'Downloading...' : 'Download King Statements'}
            </Button>
            <Button type="button" className="ml-2" onClick={async () => await handleBulkDownload('utility')} disabled={isDownloading}>
              {isDownloading ? 'Downloading...' : 'Download Utility Statements'}
            </Button>
          </div>
        )}
        <Table
          isLoading={listLoading}
          colConfig={colConfig}
          data={
            invoices ?? {
              results: [],
              total: 0,
              page: 0,
              resultsPerPage: 20,
            }
          }
          handlePaginatorClick={handlePaginatorClick}
        />
      </Layout>
    </>
  )
}

export default Invoices;
